import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { getNotifications } from "../api";

const NOTIFICATIONS_PAGE_SIZE = 10;

/** Badge / unread count — lightweight first page only */
export const useGetNotificationsBadge = (enabled = true) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ["notifications", "badge"],
    queryFn: () =>
      getNotifications({ isRead: false, page: 1, pageSize: 1 }),
    enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });

  return {
    data,
    unreadCount: data?.unread_count ?? 0,
    isLoading,
    isSuccess,
    isError,
    error,
  };
};

/** Dropdown list — 10 per page, scroll to load more */
export const useGetNotificationsInfinite = (enabled = false) => {
  const query = useInfiniteQuery({
    queryKey: ["notifications", "infinite"],
    queryFn: ({ pageParam }) =>
      getNotifications({
        isRead: true,
        page: pageParam,
        pageSize: NOTIFICATIONS_PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const page = lastPage?.pagination?.page;
      const pageCount = lastPage?.pagination?.pageCount;
      if (page != null && pageCount != null && page < pageCount) {
        return page + 1;
      }
      return undefined;
    },
    enabled,
    staleTime: 15_000,
    refetchOnWindowFocus: false,
  });

  const results = useMemo(
    () => query.data?.pages?.flatMap((page) => page?.results || []) || [],
    [query.data]
  );

  const unreadCount =
    query.data?.pages?.[query.data.pages.length - 1]?.unread_count ??
    query.data?.pages?.[0]?.unread_count ??
    0;

  return {
    ...query,
    results,
    unreadCount,
  };
};

/** @deprecated Prefer useGetNotificationsBadge / useGetNotificationsInfinite */
export const useGetNotifications = (isRead = false) => {
  const { data, isLoading, isError, error, isSuccess } = useQuery({
    queryKey: ["notifications", isRead],
    queryFn: () =>
      getNotifications({ isRead, page: 1, pageSize: NOTIFICATIONS_PAGE_SIZE }),
  });

  return { data, isLoading, isSuccess, isError, error };
};
